import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { apiClient } from "../../../apis/apiClient";
import { getVehicleQrCode } from "../../../configs/endpoints";
import { useShowToast } from "../../../hooks";

function BikeQrModal({ visible, bike, closeAction }) {
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const showToast = useShowToast();

  useEffect(() => {
    if (visible && bike) {
      loadQrCode();
    } else {
      setQrCodeData(null);
      setError(null);
    }
  }, [visible, bike]);

  const loadQrCode = async () => {
    if (!bike?.code || !bike?.city?.id) {
      setError("Vehicle code or city not available.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(
        getVehicleQrCode(bike.code, bike.city.id)
      );
      const body = response?.data?.body;
      if (body?.has_qr_code && body?.qr_code_base64) {
        setQrCodeData(body);
      } else if (
        body?.error === "WHITELABEL_NOT_FOUND" ||
        body?.error === "WHITELABEL_NOT_CONFIGURED"
      ) {
        setError("Whitelabel not configured for this city.");
        setQrCodeData(null);
      } else {
        setError("Could not generate QR code.");
        setQrCodeData(null);
      }
    } catch (err) {
      setError("Failed to load QR code. Please try again.");
      setQrCodeData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!qrCodeData?.qr_code_base64) return;

    const link = document.createElement("a");
    link.href = `data:image/png;base64,${qrCodeData.qr_code_base64}`;
    link.download = `vehicle-qr-${bike.code}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast("QR code downloaded", "success");
  };

  const handleClose = () => {
    setQrCodeData(null);
    setError(null);
    closeAction();
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      );
    }

    if (qrCodeData?.qr_code_base64) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 2,
          }}
        >
          <img
            src={`data:image/png;base64,${qrCodeData.qr_code_base64}`}
            alt={`QR Code for ${bike?.name}`}
            style={{
              maxWidth: "100%",
              height: "auto",
              border: "1px solid #ddd",
              padding: 10,
            }}
          />
          {qrCodeData?.qr_url && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, wordBreak: "break-all" }}
            >
              {qrCodeData.qr_url}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: "center", py: 2 }}>
        <Typography color="text.secondary">
          No QR code available for this vehicle.
        </Typography>
      </Box>
    );
  };

  const renderActions = () => {
    if (loading) return null;

    if (error) {
      return (
        <>
          <Button onClick={loadQrCode}>Retry</Button>
          <Button onClick={handleClose}>Close</Button>
        </>
      );
    }

    if (qrCodeData?.qr_code_base64) {
      return (
        <>
          <Button startIcon={<DownloadIcon />} onClick={handleDownload}>
            Download
          </Button>
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        </>
      );
    }

    return <Button onClick={handleClose}>Close</Button>;
  };

  return (
    <Dialog open={visible} onClose={handleClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle>Vehicle QR Code - {bike?.code}</DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>{renderActions()}</DialogActions>
    </Dialog>
  );
}

export default BikeQrModal;
